/* eslint-disable @typescript-eslint/no-explicit-any */
import { UUID } from "crypto";
import { baseApi } from "./baseApi";
import {
  PayloadBankAccount,
  PayloadPaymentDetails,
  PayloadPaymentMethod,
  Response,
  SolicitudCompleted,
  ScheduleResponse,
  GetPaymentMethodByIdResponse,
  BankAccountResponse,
  BankAccount,
  ConditionResponse,
  LoanApplication,
  getLoanCreditUseResponse,
  ScheduleVisit,
  TaxSystemConfigResponse,
  LoanCreditUseStructure,
  TaxSystemConfigByRfcResponse,
  TaxSystemConfigByRfc,
  MotorbikeDetail,
  PayloadMotorbikeDetailCreate,
  GetMotorbikeDetailResponse,
  PostMotorbikeDetailResponse,
  GetMotorbikeDetailByIdResponse,
  TablaAmortizacionMicredimoto,
  GetTablaAmortizacionMicredimotoResponse,
  CrediviveSimulacionCredito,
  GetAmountsCrediviveResponse,
} from "@interfaces/index";
import {
  LoanCondiciones,
  GetLoanConditionsResponse,
  PostValidateNextStageResponse,
  BankAccountResponsePost,
  BankAccountStatement,
  BankAccountStatementResponsePost,
  ResponsePaymentDetails,
} from "@interfaces/response";
import { PayloadBankAccountStatementPost, PayloadCondition, PayloadMotorbikeDetailUpdate } from "@interfaces/request";
import { LoanStatus } from "@interfaces/catalogs";
import { setLoanStatus } from "@store/slices/registerSlice";

const solicitudApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getLoanCreditUse: build.query<LoanCreditUseStructure, string>({
      query: (loanId) => `solicitudes/${loanId}/destino_recursos`,
      transformResponse: (response: getLoanCreditUseResponse) => {
        return response.data;
      },
    }),
    postLoanUse: build.query<Response, any>({
      query: (data: { loanId: string; body: any }) => ({
        url: `solicitudes/${data.loanId}/destino_recursos`,
        method: "POST",
        body: data.body,
      }),
    }),
    postValidateNextStage: build.mutation<
      PostValidateNextStageResponse,
      { applicationId: string; statusCode: LoanStatus | string; username: string; disable_sequence_check?: boolean }
    >({
      query: ({ applicationId, statusCode, username, disable_sequence_check }) => ({
        url: `solicitudes/${applicationId}/progreso`,
        method: "POST",
        body: {
          status_code: statusCode,
          user_name: username,
          disable_sequence_check: disable_sequence_check || undefined,
        },
      }),
      async onQueryStarted(arg, api) {
        const { statusCode } = arg;
        const previousStatus = (api.getState() as unknown as { register: { loan_status: string } }).register
          .loan_status;
        api.dispatch(setLoanStatus(statusCode as LoanStatus));
        // Si el cambio falla (p. ej. un 504), se regresa al status anterior: el que se puso al iniciar no es real
        // y, si se queda, las guardas de rutas mandan al usuario a la pantalla del status nuevo.
        try {
          await api.queryFulfilled;
        } catch {
          api.dispatch(setLoanStatus(previousStatus));
        }
      },
    }),
    getLoanApplicationById: build.query<LoanApplication, string>({
      query: (soldId) => `solicitudes?application_id=${soldId}`,
      transformResponse: (response: { data: LoanApplication[] }) => {
        if (response.data.length === 0) {
          throw { status: 404, data: { message: "No se encontró la solicitud" } };
        }
        return response.data[0];
      },
    }),
    getScheduleByApplicationId: build.query<ScheduleVisit, string>({
      query: (solicitud_id) => ({
        url: `solicitudes/${solicitud_id}/agendar_visita`,
        method: "GET",
      }),
      transformResponse: (response: ScheduleResponse) => {
        if (response.data.data.length === 0) {
          throw { status: 404, data: { message: "No se encontró una visita agendada" } };
        }
        return response.data.data[0];
      },
    }),
    getBankAccounts: build.query<BankAccount[], string>({
      query: (solicitudId: UUID) => ({
        url: `solicitudes/${solicitudId}/cuentas_bancarias`,
        method: "GET",
      }),
      transformResponse: (response: BankAccountResponse) => {
        return response.data;
      },
    }),
    postBankAccounts: build.mutation<BankAccount, { solicitudId: UUID | string; body: PayloadBankAccount }>({
      query: (data: { solicitudId: UUID; body: PayloadBankAccount }) => ({
        url: `solicitudes/${data.solicitudId}/cuentas_bancarias`,
        method: "POST",
        body: data.body,
      }),
      transformResponse: (response: BankAccountResponsePost) => {
        return response.data;
      },
    }),
    putBankAccount: build.mutation<
      BankAccount[],
      { solicitudId: UUID | string; bankAccountId: UUID | string; body: PayloadBankAccount }
    >({
      query: (data: { solicitudId: UUID | string; bankAccountId: UUID | string; body: PayloadBankAccount }) => ({
        url: `solicitudes/${data.solicitudId}/cuentas_bancarias/${data.bankAccountId}`,
        method: "PATCH",
        body: data.body,
      }),
      transformResponse: (response: BankAccountResponse) => {
        return response.data;
      },
    }),
    postBankAccountStatement: build.mutation<
      BankAccountStatement,
      { solicitudId: UUID | string; accountId: UUID | string; body: PayloadBankAccountStatementPost }
    >({
      query: ({ solicitudId, accountId, body }) => ({
        url: `solicitudes/${solicitudId}/cuentas_bancarias/${accountId}/documentos`,
        method: "POST",
        body,
      }),
      transformResponse: (response: BankAccountStatementResponsePost) => {
        return response.data;
      },
    }),
    deleteBankAccountStatement: build.mutation<
      null,
      { solicitudId: UUID | string; accountId: UUID | string; documentId: UUID | string }
    >({
      query: ({ solicitudId, accountId, documentId }) => ({
        url: `solicitudes/${solicitudId}/cuentas_bancarias/${accountId}/documentos/${documentId}`,
        method: "DELETE",
      }),
    }),
    deleteBankAccount: build.mutation<Response, { solicitudId: UUID | string; accountId: string }>({
      query: ({ solicitudId, accountId }) => ({
        url: `solicitudes/${solicitudId}/cuentas_bancarias/${accountId}`,
        method: "DELETE",
      }),
    }),
    postPaymentMethod: build.mutation<Response, { solicitudId: UUID | string; body: PayloadPaymentMethod }>({
      query: (data: { solicitudId: UUID; body: PayloadPaymentMethod }) => ({
        url: `solicitudes/${data.solicitudId}/forma_pagos`,
        method: "POST",
        body: data.body,
      }),
    }),
    getPaymentMethodById: build.query<GetPaymentMethodByIdResponse, { solicitudId: string }>({
      query: ({ solicitudId }) => ({
        url: `solicitudes/${solicitudId}/forma_pagos`,
        method: "GET",
      }),
    }),
    postPaymentDetails: build.mutation<
      ResponsePaymentDetails,
      { solicitudId: UUID | string; body: PayloadPaymentDetails }
    >({
      query: (data: { solicitudId: UUID; body: PayloadPaymentDetails }) => ({
        url: `solicitudes/${data.solicitudId}/detalles`,
        method: "POST",
        body: data.body,
      }),
    }),
    getPaymentDetails: build.query<ResponsePaymentDetails, { solicitudId: string }>({
      query: (data: { solicitudId: string }) => ({
        url: `solicitudes/${data.solicitudId}/detalles`,
        method: "GET",
      }),
    }),
    putPaymentDetails: build.mutation<
      ResponsePaymentDetails,
      { solicitudId: UUID | string; detailId: string; body: PayloadPaymentDetails }
    >({
      query: (data: { solicitudId: UUID; detailId: UUID; body: PayloadPaymentDetails }) => ({
        url: `solicitudes/${data.solicitudId}/detalles/${data.detailId}`,
        method: "PUT",
        body: data.body,
      }),
    }),
    putApplicationCompleted: build.mutation<Response, { id_sol: string; body: SolicitudCompleted }>({
      query: ({ id_sol, body }) => ({
        url: `solicitudes/${id_sol}`,
        method: "PUT",
        body,
      }),
    }),
    getConditionsById: build.query<LoanCondiciones[], string>({
      query: (soldId) => `solicitudes/${soldId}/condiciones?q=active:true`,
      transformResponse: (response: { data: any }) => {
        return response.data.data;
      },
    }),
    getLoanApplicationConditionsById: build.query<LoanCondiciones, string>({
      query: (soldId) => `solicitudes/${soldId}/condiciones?q=active:true`,
      transformResponse: (response: GetLoanConditionsResponse) => {
        if (response.data.data.length === 0) {
          throw { status: 404, data: { message: "No se encontraron condiciones activas" } };
        }
        return response.data.data[0];
      },
    }),
    postCondition: build.mutation<ConditionResponse, PayloadCondition>({
      query: ({ loan_application, ...body }) => ({
        url: `solicitudes/${loan_application}/condiciones`,
        method: "POST",
        body,
      }),
    }),
    putCondition: build.mutation<ConditionResponse, { body: PayloadCondition; id: string }>({
      query: ({ body, id }) => ({
        url: `solicitudes/${body.loan_application}/condiciones/${id}`,
        method: "PUT",
        body,
      }),
    }),

    getTaxSystemConfig: build.query<TaxSystemConfigResponse, string>({
      query: (code) => `catalogos/tax_systems?code=${code}`,
      transformResponse: (response: { data: any }) => {
        const response_data = response.data.data[0];
        return {
          id: response_data.id,
          code: response_data.code,
          product_id: response_data.product_id,
          minAmount: response_data.min_amount,
          maxAmount: response_data.max_amount,
          minTerm: response_data.min_term,
          maxTerm: response_data.max_term,
          stepAmount: response_data.jump_amount ?? 50000,
          stepTerm: response_data.jump_term ?? 3,
          tax_system_type: response_data.tax_system_type,
          defaultAmount: response_data.default_amount,
          defaultTerm: response_data.default_term,
          minAdvance: response_data.min_advance_percentage || 0,
          maxAdvance: response_data.max_advance_percentage || 0,
          stepAdvance: response_data.jump_advance_percentage || 0,
          defaultAdvance: response_data.default_advance_percentage || 0,
          minResidual: response_data.min_residual_percentage || 0,
          maxResidual: response_data.max_residual_percentage || 0,
          stepResidual: response_data.jump_residual_percentage || 0,
          defaultResidual: response_data.default_residual_percentage || 0,
        };
      },
    }),
    getTaxSystemConfigByRfc: build.query<TaxSystemConfigByRfc[], string>({
      query: (rfc) => `catalogos/tax_systems_by_rfc/${rfc}`,
      transformResponse: (response: TaxSystemConfigByRfcResponse) => {
        return response.data.data;
      },
    }),
    postCloneLoanApplication: build.mutation<Response, { solicitud_id: string }>({
      query: ({ solicitud_id }) => ({
        url: `solicitudes/${solicitud_id}/clone`,
        method: "POST",
      }),
    }),
    getMotorbikeDetail: build.query<MotorbikeDetail[], { solicitudId: string }>({
      query: ({ solicitudId }) => ({
        url: `solicitudes/${solicitudId}/detalle_moto`,
        method: "GET",
      }),
      transformResponse: (response: GetMotorbikeDetailResponse) => {
        return response?.data?.data;
      },
    }),
    getMotorbikeDetailById: build.query<MotorbikeDetail, { solicitudId: string; motorbikeDetailId: string }>({
      query: ({ solicitudId, motorbikeDetailId }) => ({
        url: `solicitudes/${solicitudId}/detalle_moto/${motorbikeDetailId}`,
        method: "GET",
      }),
      transformResponse: (response: GetMotorbikeDetailByIdResponse) => {
        return response.data;
      },
    }),
    postMotorbikeDetail: build.mutation<MotorbikeDetail, { solicitudId: string; body: PayloadMotorbikeDetailCreate }>({
      query: ({ solicitudId, body }) => ({
        url: `solicitudes/${solicitudId}/detalle_moto`,
        method: "POST",
        body: {
          loan_application_id: solicitudId,
          ...body,
        },
      }),
      transformResponse: (response: PostMotorbikeDetailResponse) => {
        return response.data;
      },
    }),
    putMotorbikeDetail: build.mutation<
      MotorbikeDetail,
      { solicitudId: string; motorbikeDetailId: string; body: PayloadMotorbikeDetailUpdate }
    >({
      query: ({ solicitudId, motorbikeDetailId, body }) => ({
        url: `solicitudes/${solicitudId}/detalle_moto/${motorbikeDetailId}`,
        method: "PATCH",
        body: {
          loan_application_id: solicitudId,
          ...body,
        },
      }),
      transformResponse: (response: PostMotorbikeDetailResponse) => {
        return response.data;
      },
    }),
    getTablaAmortizacionMicredimoto: build.query<TablaAmortizacionMicredimoto, { solicitudId: string }>({
      query: ({ solicitudId }) => ({
        url: `micredimoto/${solicitudId}/tabla_amortizacion`,
        method: "GET",
      }),
      transformResponse: (response: GetTablaAmortizacionMicredimotoResponse) => {
        return response.data;
      },
    }),
    getAmountsCredivive: build.query<CrediviveSimulacionCredito, { monto: number; plazo: number; tasa: number }>({
      query: ({ monto, plazo, tasa }) => ({
        url: `credivive/simulacion_credito`,
        method: "POST",
        body: {
          monto,
          plazo,
          tasa,
        },
      }),
      transformResponse: (response: GetAmountsCrediviveResponse) => {
        return response.data;
      },
    }),
  }),
});

export const {
  useLazyGetLoanCreditUseQuery,
  useLazyPostLoanUseQuery,
  usePostValidateNextStageMutation,
  useLazyGetScheduleByApplicationIdQuery,
  usePostBankAccountsMutation,
  usePutBankAccountMutation,
  usePostBankAccountStatementMutation,
  useDeleteBankAccountStatementMutation,
  usePostPaymentMethodMutation,
  usePostPaymentDetailsMutation,
  usePutPaymentDetailsMutation,
  useDeleteBankAccountMutation,
  usePutApplicationCompletedMutation,
  useLazyGetPaymentDetailsQuery,
  useLazyGetPaymentMethodByIdQuery,
  useLazyGetBankAccountsQuery,
  useLazyGetConditionsByIdQuery, // TODO: cambiar por useLazyGetLoanApplicationConditionsByIdQuery
  useLazyGetLoanApplicationConditionsByIdQuery,
  usePostConditionMutation,
  useLazyGetLoanApplicationByIdQuery,
  useLazyGetTaxSystemConfigQuery,
  useLazyGetTaxSystemConfigByRfcQuery,
  usePostCloneLoanApplicationMutation,
  usePutConditionMutation,
  usePostMotorbikeDetailMutation,
  usePutMotorbikeDetailMutation,
  useLazyGetMotorbikeDetailQuery,
  useLazyGetMotorbikeDetailByIdQuery,
  useLazyGetTablaAmortizacionMicredimotoQuery,
  useLazyGetAmountsCrediviveQuery,
} = solicitudApi;
